import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { TimePicker } from '@/components/ui/time-picker';
import { Plus, Building2, Upload, Sparkles, FileText, X, Loader2 } from 'lucide-react';
import { DatePickerField } from '@/components/shipment';
import { AddressAutocomplete } from '@/components/ui/address-autocomplete';
import { AIFieldBadge } from '../AIFieldBadge';

// Label + inline AI badge for fields that may be auto-filled by Gemini.
// `aiBadges` is the shipment's `ai_autofilled_fields` map. Render nothing if absent.
const LabelWithAI = ({ children, field, aiBadges, shipmentId, api, onDismissed, className = 'text-slate-300' }) => (
  <Label className={`${className} flex items-center gap-2`}>
    <span>{children}</span>
    <AIFieldBadge
      meta={aiBadges?.[field]}
      shipmentId={shipmentId}
      field={field}
      api={api}
      onDismissed={onDismissed}
    />
  </Label>
);

/**
 * LoadFormSections - Shared form sections for AddLoadSheet and EditLoadSheet
 * These components can be used to build consistent load forms across the app
 */

// Section Header Component
export const SectionHeader = ({ title, icon: Icon, className = '' }) => (
  <h3 className={`font-heading font-medium text-white border-b border-slate-800 pb-2 flex items-center gap-2 ${className}`}>
    {Icon && <Icon className="h-4 w-4" />}
    {title}
  </h3>
);

// AI Document Upload Section
export const AIDocumentUploadSection = ({ 
  uploadingDO, 
  uploadedDOFile, 
  setUploadedDOFile, 
  doFileInputRef, 
  handleDOUpload,
  title = 'Upload Delivery Order',
  description = 'Upload a DO image to auto-fill fields with AI'
}) => (
  <div className="p-4 bg-gradient-to-r from-blue-900/30 to-purple-900/30 rounded-lg border border-blue-500/30">
    <div className="flex items-center gap-3 mb-3">
      <Sparkles className="h-5 w-5 text-blue-400" />
      <h3 className="font-heading font-medium text-white">{title}</h3>
    </div>
    <p className="text-slate-400 text-sm mb-3">{description}</p>
    <div className="flex items-center gap-3">
      <input type="file" ref={doFileInputRef} onChange={handleDOUpload} accept=".pdf,.png,.jpg,.jpeg" className="hidden" />
      <Button
        type="button"
        onClick={() => doFileInputRef.current?.click()}
        disabled={uploadingDO}
        variant="outline"
        className="border-blue-500/50 hover:border-blue-400 hover:bg-blue-500/10"
        data-testid="upload-do-btn"
      >
        {uploadingDO ? (
          <><Loader2 className="h-4 w-4 animate-spin mr-2" />Processing...</>
        ) : (
          <><Upload className="h-4 w-4 mr-2" />Upload DO</>
        )}
      </Button>
      {uploadedDOFile && !uploadingDO && (
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <FileText className="h-4 w-4" />
          <span>{uploadedDOFile.name}</span>
          <Button type="button" variant="ghost" size="sm" onClick={() => setUploadedDOFile(null)} className="h-6 w-6 p-0 text-slate-400 hover:text-white">
            <X className="h-3 w-3" />
          </Button>
        </div>
      )}
    </div>
    <p className="text-slate-500 text-xs mt-2">Supports PDF, PNG, JPG files</p>
  </div>
);

// Container Info Section
export const ContainerInfoSection = ({ formData, handleChange, masterData, t, aiBadges, shipmentId, api, onAIDismissed }) => (
  <div className="space-y-4">
    <SectionHeader title={t?.('modals.addLoad.containerInfo') || 'Container Information'} />
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <LabelWithAI field="container_number" aiBadges={aiBadges} shipmentId={shipmentId} api={api} onDismissed={onAIDismissed}>
          {t?.('shipments.container') || 'Container'} *
        </LabelWithAI>
        <Input
          value={formData.container_number || ''}
          onChange={(e) => handleChange('container_number', e.target.value.toUpperCase())}
          placeholder="ABCD1234567"
          className="bg-slate-950 border-slate-800 text-white font-mono"
          data-testid="container-number-input"
        />
      </div>
      <div className="space-y-2">
        <Label className="text-slate-300">Type</Label>
        <Select value={formData.shipment_type} onValueChange={(v) => handleChange('shipment_type', v)}>
          <SelectTrigger className="bg-slate-950 border-slate-800 text-white"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-800">
            <SelectItem value="IMPORT" className="text-white">Import</SelectItem>
            <SelectItem value="EXPORT" className="text-white">Export</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label className="text-slate-300">Size</Label>
        <Select value={formData.container_size} onValueChange={(v) => handleChange('container_size', v)}>
          <SelectTrigger className="bg-slate-950 border-slate-800 text-white"><SelectValue placeholder="Select size" /></SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-800">
            {masterData.container_types?.length > 0 ? (
              masterData.container_types.map(ct => (
                <SelectItem key={ct.id} value={ct.code} className="text-white">{ct.code} - {ct.name}</SelectItem>
              ))
            ) : (
              <>
                <SelectItem value="20GP" className="text-white">20GP (Standard)</SelectItem>
                <SelectItem value="40GP" className="text-white">40GP (Standard)</SelectItem>
                <SelectItem value="40HQ" className="text-white">40HQ (High Cube)</SelectItem>
                <SelectItem value="45HQ" className="text-white">45HQ (High Cube)</SelectItem>
              </>
            )}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label className="text-slate-300">SSL</Label>
        <Select value={formData.ssl_id} onValueChange={(v) => handleChange('ssl_id', v)}>
          <SelectTrigger className="bg-slate-950 border-slate-800 text-white"><SelectValue placeholder="Select SSL" /></SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-800">
            {masterData.ssls?.length > 0 ? (
              masterData.ssls.map(ssl => (
                <SelectItem key={ssl.id} value={ssl.id} className="text-white">{ssl.code || ssl.name}</SelectItem>
              ))
            ) : (
              <SelectItem value="none" disabled className="text-slate-500">No SSLs available</SelectItem>
            )}
          </SelectContent>
        </Select>
      </div>
    </div>
  </div>
);

// Terminal & Chassis Section
export const TerminalChassisSection = ({ formData, handleChange, masterData, aiBadges, shipmentId, api, onAIDismissed }) => (  <div className="space-y-4">
    <SectionHeader title="Terminal & Equipment" />
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <LabelWithAI field="terminal_id" aiBadges={aiBadges} shipmentId={shipmentId} api={api} onDismissed={onAIDismissed}>
          Pickup Terminal
        </LabelWithAI>
        <Select value={formData.terminal_id} onValueChange={(v) => handleChange('terminal_id', v)}>
          <SelectTrigger className="bg-slate-950 border-slate-800 text-white" data-testid="pickup-terminal-select"><SelectValue placeholder="Select pickup terminal" /></SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-800 max-h-[300px]">
            {(!masterData.terminals || masterData.terminals.length === 0) ? (
              <SelectItem value="none" disabled className="text-slate-500">No terminals available</SelectItem>
            ) : (
              masterData.terminals.map(terminal => (
                <SelectItem key={terminal.id} value={terminal.id} className="text-white">{terminal.name}</SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label className="text-slate-300">Return Terminal</Label>
        <Select value={formData.return_location || formData.empty_return_location_id || 'none'} onValueChange={(v) => {
          const value = v === 'none' ? '' : v;
          handleChange('return_location', value);
          handleChange('empty_return_location_id', value);
        }}>
          <SelectTrigger className="bg-slate-950 border-slate-800 text-white" data-testid="return-terminal-select"><SelectValue placeholder="Select return terminal" /></SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-800 max-h-[300px]">
            <SelectItem value="none" className="text-slate-400">None</SelectItem>
            {(masterData.terminals || []).map(terminal => (
              <SelectItem key={terminal.id} value={terminal.id} className="text-white">{terminal.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label className="text-slate-300">Yard</Label>
        <Select value={formData.yard_id} onValueChange={(v) => handleChange('yard_id', v)}>
          <SelectTrigger className="bg-slate-950 border-slate-800 text-white"><SelectValue placeholder="Select yard" /></SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-800">
            {(!masterData.yards || masterData.yards.length === 0) ? (
              <SelectItem value="none" disabled className="text-slate-500">No yards available</SelectItem>
            ) : (
              masterData.yards.map(yard => (
                <SelectItem key={yard.id} value={yard.id} className="text-white">{yard.name}</SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label className="text-slate-300">Chassis Type</Label>
        <Select value={formData.chassis_type} onValueChange={(v) => handleChange('chassis_type', v)}>
          <SelectTrigger className="bg-slate-950 border-slate-800 text-white"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-800">
            {masterData.chassis_types?.length > 0 ? (
              masterData.chassis_types.map(ct => (
                <SelectItem key={ct.id} value={ct.code} className="text-white">{ct.name}</SelectItem>
              ))
            ) : (
              <>
                <SelectItem value="OWN" className="text-white">OWN</SelectItem>
                <SelectItem value="POOL" className="text-white">POOL</SelectItem>
              </>
            )}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <LabelWithAI field="chassis_number" aiBadges={aiBadges} shipmentId={shipmentId} api={api} onDismissed={onAIDismissed}>
          Chassis Number
        </LabelWithAI>
        <Input value={formData.chassis_number || ''} onChange={(e) => handleChange('chassis_number', e.target.value)} placeholder="Chassis #" className="bg-slate-950 border-slate-800 text-white" />
      </div>
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <LabelWithAI field="mbl" aiBadges={aiBadges} shipmentId={shipmentId} api={api} onDismissed={onAIDismissed}>
          MBL
        </LabelWithAI>
        <Input 
          value={formData.mbl || ''} 
          onChange={(e) => {
            const mblValue = e.target.value;
            handleChange('mbl', mblValue);
            // Auto-fill PIN with last 4 digits (or 5 for ITS terminal). We
            // resolve the terminal NAME from masterData by terminal_id since
            // the dropdown only stores the id and formData.terminal_name is
            // often empty on a fresh pick.
            if (mblValue && mblValue.length >= 4) {
              const term = (masterData?.terminals || []).find(t => t.id === formData.terminal_id);
              const terminalName = ((term?.name || formData.terminal_name) || '').toLowerCase();
              const isITS = terminalName.includes('its') || terminalName.includes('international transportation');
              const pinDigits = isITS ? 5 : 4;
              const pin = mblValue.slice(-pinDigits);
              handleChange('pin', pin);
            }
          }} 
          placeholder="MBL number" 
          className="bg-slate-950 border-slate-800 text-white" 
        />
        <p className="text-xs text-slate-500">PIN auto-fills from last {(((masterData?.terminals || []).find(t => t.id === formData.terminal_id)?.name) || formData.terminal_name || '').toLowerCase().includes('its') ? '5' : '4'} digits</p>
      </div>
      <div className="space-y-2">
        <LabelWithAI field="pin" aiBadges={aiBadges} shipmentId={shipmentId} api={api} onDismissed={onAIDismissed}>
          PIN
        </LabelWithAI>
        <Input value={formData.pin || ''} onChange={(e) => handleChange('pin', e.target.value)} placeholder="PIN (auto-filled from MBL)" className="bg-slate-950 border-slate-800 text-white" />
      </div>
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <LabelWithAI field="seal_number" aiBadges={aiBadges} shipmentId={shipmentId} api={api} onDismissed={onAIDismissed}>
          Seal Number
        </LabelWithAI>
        <Input
          value={formData.seal_number || ''}
          onChange={(e) => handleChange('seal_number', e.target.value.toUpperCase())}
          placeholder="Seal #"
          className="bg-slate-950 border-slate-800 text-white font-mono"
          data-testid="seal-number-input"
        />
      </div>
      <div className="space-y-2">
        <LabelWithAI field="job_number" aiBadges={aiBadges} shipmentId={shipmentId} api={api} onDismissed={onAIDismissed}>
          Job Number
        </LabelWithAI>
        <Input
          value={formData.job_number || ''}
          onChange={(e) => handleChange('job_number', e.target.value.toUpperCase())}
          placeholder="LOC-YYYY-XXXX"
          className="bg-slate-950 border-slate-800 text-white font-mono"
          data-testid="job-number-input"
        />
      </div>
    </div>
  </div>
);

// Dates Section
export const DatesSection = ({ formData, handleChange, masterData, aiBadges, shipmentId, api, onAIDismissed }) => (
  <div className="space-y-4">
    <SectionHeader title="Dates" />
    <div className="grid grid-cols-2 gap-4">
      <DatePickerField label="ETA" value={formData.eta} onChange={handleChange} field="eta" />
      <DatePickerField label="Last Free Date" value={formData.last_free_date || formData.lfd} onChange={handleChange} field="last_free_date" />
      <DatePickerField label="LFD Yard" value={formData.lfd_yard} onChange={handleChange} field="lfd_yard" />
      <DatePickerField label="Pickup Date" value={formData.pickup_date} onChange={handleChange} field="pickup_date" />
      <DatePickerField label="Delivery Date" value={formData.delivery_date} onChange={handleChange} field="delivery_date" />
      <DatePickerField label="MTY RDY" value={formData.mty_rdy} onChange={handleChange} field="mty_rdy" />
    </div>
    <div className="grid grid-cols-3 gap-4">
      <div className="relative">
        <DatePickerField label="Appointment Date" value={formData.appointment_date} onChange={handleChange} field="appointment_date" />
        {aiBadges?.appointment_date && (
          <div className="absolute top-0 right-0">
            <AIFieldBadge
              meta={aiBadges.appointment_date}
              shipmentId={shipmentId}
              field="appointment_date"
              api={api}
              onDismissed={onAIDismissed}
            />
          </div>
        )}
      </div>
      <div className="space-y-2">
        <Label className="text-slate-300">From Time</Label>
        <TimePicker
          value={formData.appointment_time_from || ''}
          onChange={(time) => {
            handleChange('appointment_time_from', time);
            // If the user is setting an appointment time but never opened the
            // date picker, default the date to today — otherwise the date
            // picker's "today" placeholder is misleading and the row would
            // save with no appointment_date (showing '-' on the dashboard).
            if (time && !formData.appointment_date) {
              handleChange('appointment_date', new Date());
            }
            if (time) {
              const [hours, minutes] = time.split(':').map(Number);
              const toHours = (hours + 1) % 24;
              handleChange('appointment_time_to', `${String(toHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`);
            }
          }}
          placeholder="From"
        />
      </div>
      <div className="space-y-2">
        <Label className="text-slate-300">To Time</Label>
        <TimePicker value={formData.appointment_time_to} onChange={(time) => handleChange('appointment_time_to', time)} placeholder="To" />
      </div>
    </div>
    <div className="grid grid-cols-2 gap-4">
      <DatePickerField label="MTY Return Date" value={formData.mty_return} onChange={handleChange} field="mty_return" />
    </div>
  </div>
);

// Fees Section
export const FeesSection = ({ formData, handleChange }) => (
  <div className="space-y-4">
    <SectionHeader title="Fees" />
    <div className="grid grid-cols-3 gap-4">
      <div className="space-y-2">
        <Label className="text-slate-300">Pier Pass (PP)</Label>
        <div className="flex items-center gap-2">
          <Input type="number" value={formData.pier_pass ?? ''} onChange={(e) => handleChange('pier_pass', parseFloat(e.target.value) || 0)} className="bg-slate-950 border-slate-800 text-white w-24" />
          <div className="flex items-center gap-2">
            <Checkbox checked={formData.pier_pass_paid} onCheckedChange={(c) => handleChange('pier_pass_paid', c)} className="border-slate-600" />
            <Label className="text-slate-400 text-xs">Paid</Label>
          </div>
        </div>
      </div>
      <div className="space-y-2">
        <Label className="text-slate-300">CTF Charge</Label>
        <div className="flex items-center gap-2">
          <Input type="number" value={formData.ctf_charge ?? ''} onChange={(e) => handleChange('ctf_charge', parseFloat(e.target.value) || 0)} className="bg-slate-950 border-slate-800 text-white w-24" />
          <div className="flex items-center gap-2">
            <Checkbox checked={formData.ctf_paid} onCheckedChange={(c) => handleChange('ctf_paid', c)} className="border-slate-600" />
            <Label className="text-slate-400 text-xs">Paid</Label>
          </div>
        </div>
      </div>
      
      {(formData.pier_pass_paid || formData.ctf_paid) && (
        <div className="space-y-2">
          <Label className="text-slate-300">PP/CTF Paid By</Label>
          <Select value={formData.fees_paid_by || "none"} onValueChange={(v) => handleChange('fees_paid_by', v === "none" ? "" : v)}>
            <SelectTrigger className="bg-slate-950 border-slate-800 text-white"><SelectValue placeholder="Select who paid" /></SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-800">
              <SelectItem value="none" className="text-slate-400">Select</SelectItem>
              <SelectItem value="Customer" className="text-white">Customer</SelectItem>
              <SelectItem value="Company" className="text-white">Company</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
      
      <div className="space-y-2">
        <Label className="text-amber-400">OW (Overweight)</Label>
        <div className="flex items-center gap-2">
          <Input type="number" value={formData.overweight_fee ?? ''} onChange={(e) => handleChange('overweight_fee', parseFloat(e.target.value) || 0)} className="bg-slate-950 border-amber-500/50 text-white w-24" data-testid="overweight-fee-input" />
          <div className="flex items-center gap-2">
            <Checkbox checked={formData.is_overweight} onCheckedChange={(c) => handleChange('is_overweight', c)} className={`border-amber-500 ${formData.is_overweight ? 'bg-red-500 border-red-500' : ''}`} data-testid="overweight-checkbox" />
            <Label className={`text-xs ${formData.is_overweight ? 'text-red-400 font-bold' : 'text-amber-400'}`}>OW</Label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox checked={formData.is_hazmat} onCheckedChange={(c) => handleChange('is_hazmat', c)} className={`border-orange-500 ${formData.is_hazmat ? 'bg-orange-500 border-orange-500' : ''}`} data-testid="hazmat-checkbox" />
            <Label className={`text-xs ${formData.is_hazmat ? 'text-orange-400 font-bold' : 'text-orange-400/60'}`}>HZ</Label>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Options & Remarks Section
export const OptionsRemarksSection = ({ formData, handleChange }) => (
  <div className="space-y-4">
    <div className="flex items-center gap-2">
      <Checkbox id="is_important" checked={formData.is_important} onCheckedChange={(checked) => handleChange('is_important', checked)} className="border-slate-600" />
      <Label htmlFor="is_important" className="text-red-400 font-medium">IMPORTANT SHIPMENT</Label>
    </div>
    <div className="space-y-2">
      <Label className="text-slate-300">Remarks</Label>
      <Textarea value={formData.remarks || ''} onChange={(e) => handleChange('remarks', e.target.value)} placeholder="Additional notes..." className="bg-slate-950 border-slate-800 text-white min-h-[80px]" />
    </div>
  </div>
);

// Multiple Stop Section
export const MultipleStopSection = ({ formData, handleChange }) => (
  <>
    <div className="flex items-center gap-2">
      <Checkbox id="is_multiple_stop" checked={formData.is_multiple_stop} onCheckedChange={(c) => handleChange('is_multiple_stop', c)} className="border-slate-600" />
      <Label htmlFor="is_multiple_stop" className="text-orange-400 font-medium cursor-pointer">Multiple Stop Load</Label>
    </div>
    {formData.is_multiple_stop && (
      <div className="p-4 bg-orange-900/20 rounded-lg border border-orange-500/30 space-y-3 mt-2">
        <Label className="text-orange-400 font-medium">Secondary Stop</Label>
        <div className="grid grid-cols-2 gap-3">
          <Input value={formData.secondary_address || ''} onChange={(e) => handleChange('secondary_address', e.target.value)} placeholder="Address" className="bg-slate-950 border-slate-800 text-white col-span-2" />
          <Input value={formData.secondary_city || ''} onChange={(e) => handleChange('secondary_city', e.target.value)} placeholder="City" className="bg-slate-950 border-slate-800 text-white" />
          <Input value={formData.secondary_state || ''} onChange={(e) => handleChange('secondary_state', e.target.value)} placeholder="State" className="bg-slate-950 border-slate-800 text-white" />
          <Input value={formData.secondary_zip || ''} onChange={(e) => handleChange('secondary_zip', e.target.value)} placeholder="ZIP" className="bg-slate-950 border-slate-800 text-white" />
          <Input value={formData.secondary_contact || ''} onChange={(e) => handleChange('secondary_contact', e.target.value)} placeholder="Contact" className="bg-slate-950 border-slate-800 text-white" />
          <Input value={formData.secondary_phone || ''} onChange={(e) => handleChange('secondary_phone', e.target.value)} placeholder="Phone" className="bg-slate-950 border-slate-800 text-white" />
        </div>
      </div>
    )}
  </>
);

// Additional Info Section
export const AdditionalInfoSection = ({ formData, handleChange }) => (
  <div className="space-y-4">
    <div className="space-y-2">
      <Label className="text-slate-300">Additional Client Info</Label>
      <Textarea value={formData.additional_client_info || ''} onChange={(e) => handleChange('additional_client_info', e.target.value)} placeholder="Any additional client information..." className="bg-slate-950 border-slate-800 text-white min-h-[60px]" data-testid="additional-client-info-input" />
    </div>
    <div className="space-y-2">
      <Label className="text-slate-300">Forwarder Reference</Label>
      <Input value={formData.forwarder_reference || ''} onChange={(e) => handleChange('forwarder_reference', e.target.value)} placeholder="Forwarder reference number" className="bg-slate-950 border-slate-800 text-white" data-testid="forwarder-reference-input" />
    </div>
  </div>
);

// Customer & Delivery Section
export const LoadCustomerDeliverySection = ({ 
  formData, 
  handleChange, 
  selectedCustomer, 
  customers = [], 
  recentCustomerIds = [],
  showNewCustomer, 
  setShowNewCustomer, 
  newCustomerName, 
  setNewCustomerName, 
  handleCreateCustomer,
  showNewAddress, 
  setShowNewAddress, 
  newAddress, 
  setNewAddress, 
  handleCreateAddress, 
  handleAddressSelect
}) => {
  const recentCustomers = customers.filter(c => recentCustomerIds.includes(c.id));
  const otherCustomers = customers.filter(c => !recentCustomerIds.includes(c.id));
  
  return (
  <div className="space-y-4">
    <SectionHeader title="Customer & Delivery" icon={Building2} />
    
    {/* Customer Selection */}
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-slate-300">Customer</Label>
        <Button type="button" variant="ghost" size="sm" onClick={() => setShowNewCustomer(true)} className="text-blue-400 hover:text-blue-300 h-auto p-0">
          <Plus className="h-3 w-3 mr-1" />New Customer
        </Button>
      </div>
      <Select value={formData.customer_id} onValueChange={(v) => handleChange('customer_id', v)}>
        <SelectTrigger className="bg-slate-950 border-slate-800 text-white" data-testid="customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
        <SelectContent className="bg-slate-900 border-slate-800 max-h-[200px]">
          {recentCustomers.length > 0 && (
            <>
              <div className="px-2 py-1 text-xs text-slate-500 font-medium">Recent</div>
              {recentCustomers.map(customer => (
                <SelectItem key={customer.id} value={customer.id} className="text-white">{customer.name} {customer.code ? `(${customer.code})` : ''}</SelectItem>
              ))}
              <div className="border-t border-slate-800 my-1" />
              <div className="px-2 py-1 text-xs text-slate-500 font-medium">All Customers</div>
            </>
          )}
          {otherCustomers.map(customer => (
            <SelectItem key={customer.id} value={customer.id} className="text-white">{customer.name} {customer.code ? `(${customer.code})` : ''}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>

    {/* New Customer Inline Form */}
    {showNewCustomer && (
      <div className="p-3 bg-slate-800/50 rounded-sm space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-slate-300 text-sm">New Customer</Label>
          <Button type="button" variant="ghost" size="sm" onClick={() => setShowNewCustomer(false)} className="text-slate-400 h-auto p-1"><X className="h-3 w-3" /></Button>
        </div>
        <div className="flex gap-2">
          <Input value={newCustomerName} onChange={(e) => setNewCustomerName(e.target.value)} onKeyDown={(e) => e.stopPropagation()} placeholder="Customer name" className="bg-slate-950 border-slate-800 text-white flex-1" autoFocus />
          <Button type="button" onClick={handleCreateCustomer} size="sm" className="bg-blue-600 hover:bg-blue-500">Add</Button>
        </div>
      </div>
    )}

    {/* Address Selection */}
    {selectedCustomer && (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-slate-300">Delivery Address</Label>
          <Button type="button" variant="ghost" size="sm" onClick={() => setShowNewAddress(true)} className="text-blue-400 hover:text-blue-300 h-auto p-0">
            <Plus className="h-3 w-3 mr-1" />New Address
          </Button>
        </div>
        <Select value={formData.address_id} onValueChange={(v) => handleAddressSelect(v)}>
          <SelectTrigger className="bg-slate-950 border-slate-800 text-white" data-testid="address-select"><SelectValue placeholder="Select delivery address" /></SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-800 max-h-[200px]">
            {selectedCustomer.addresses?.map(addr => (
              <SelectItem key={addr.id} value={addr.id} className="text-white">
                <div className="flex flex-col">
                  <span>{addr.label || addr.address}</span>
                  <span className="text-xs text-slate-400">{addr.city}, {addr.state} {addr.zip_code}</span>
                </div>
              </SelectItem>
            ))}
            {(!selectedCustomer.addresses || selectedCustomer.addresses.length === 0) && (
              <div className="p-2 text-slate-400 text-sm">No addresses. Add one above.</div>
            )}
          </SelectContent>
        </Select>
      </div>
    )}

    {/* New Address Inline Form */}
    {showNewAddress && selectedCustomer && (
      <div className="p-3 bg-slate-800/50 rounded-sm space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-slate-300 text-sm">New Address for {selectedCustomer.name}</Label>
          <Button type="button" variant="ghost" size="sm" onClick={() => setShowNewAddress(false)} className="text-slate-400 h-auto p-1"><X className="h-3 w-3" /></Button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Input value={newAddress.label} onChange={(e) => setNewAddress({...newAddress, label: e.target.value})} placeholder="Label (e.g., Warehouse)" className="bg-slate-950 border-slate-800 text-white col-span-2" />
          <div className="col-span-2">
            <AddressAutocomplete
              value={newAddress.address}
              onChange={(val) => setNewAddress({...newAddress, address: val})}
              onAddressSelect={(addr) => setNewAddress({...newAddress, address: addr.street || addr.formatted_address, city: addr.city || newAddress.city, state: addr.state || newAddress.state, zip_code: addr.zip || newAddress.zip_code})}
              placeholder="Street address * (start typing for suggestions)"
            />
          </div>
          <Input value={newAddress.city} onChange={(e) => setNewAddress({...newAddress, city: e.target.value})} placeholder="City *" className="bg-slate-950 border-slate-800 text-white" />
          <Input value={newAddress.state} onChange={(e) => setNewAddress({...newAddress, state: e.target.value})} placeholder="State *" className="bg-slate-950 border-slate-800 text-white" />
          <Input value={newAddress.zip_code} onChange={(e) => setNewAddress({...newAddress, zip_code: e.target.value})} placeholder="ZIP *" className="bg-slate-950 border-slate-800 text-white" />
          <Input value={newAddress.contact_name} onChange={(e) => setNewAddress({...newAddress, contact_name: e.target.value})} placeholder="Contact name" className="bg-slate-950 border-slate-800 text-white" />
          <Input value={newAddress.contact_phone} onChange={(e) => setNewAddress({...newAddress, contact_phone: e.target.value})} placeholder="Contact phone" className="bg-slate-950 border-slate-800 text-white" />
          <div className="col-span-2">
            <Label className="text-slate-400 text-xs mb-1 block">Receiving Hours</Label>
            <div className="flex items-center gap-2">
              <TimePicker value={newAddress.receiving_hours_from} onChange={(t) => setNewAddress({...newAddress, receiving_hours_from: t})} placeholder="From" className="w-32" />
              <span className="text-slate-400 text-xs">to</span>
              <TimePicker value={newAddress.receiving_hours_to} onChange={(t) => setNewAddress({...newAddress, receiving_hours_to: t})} placeholder="To" className="w-32" />
            </div>
          </div>
          <Input value={newAddress.remark} onChange={(e) => setNewAddress({...newAddress, remark: e.target.value})} placeholder="Remark (gate code, dock, etc.)" className="bg-slate-950 border-slate-800 text-white col-span-2" />
          <div className="flex items-center gap-2">
            <Checkbox checked={newAddress.is_default} onCheckedChange={(c) => setNewAddress({...newAddress, is_default: c})} className="border-slate-600" />
            <Label className="text-slate-400 text-xs">Default address</Label>
          </div>
        </div>
        <Button type="button" onClick={handleCreateAddress} size="sm" className="bg-blue-600 hover:bg-blue-500 w-full">Add Address</Button>
      </div>
    )}

    {/* Show selected address details */}
    {formData.address_id && (
      <div className="p-3 bg-slate-800/30 rounded-sm text-sm text-slate-300">
        <div>{formData.delivery_address}</div>
        <div>{formData.delivery_city}, {formData.delivery_state} {formData.delivery_zip}</div>
        {formData.delivery_contact && <div>Contact: {formData.delivery_contact}</div>}
        {formData.delivery_phone && <div>Phone: {formData.delivery_phone}</div>}
      </div>
    )}

    {/* Multiple Stop Toggle */}
    <div className="flex items-center gap-3 py-2">
      <Checkbox id="is_multiple_stop" checked={formData.is_multiple_stop} onCheckedChange={(checked) => handleChange('is_multiple_stop', checked)} className="border-slate-600" data-testid="multiple-stop-toggle" />
      <Label htmlFor="is_multiple_stop" className="text-orange-400 font-medium cursor-pointer">Multiple Stop Load</Label>
    </div>

    {/* Secondary Delivery Address */}
    {formData.is_multiple_stop && (
      <div className="p-3 bg-orange-900/20 rounded-sm border border-orange-500/30 space-y-3">
        <Label className="text-orange-400 text-sm font-medium">Secondary Delivery Address</Label>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <AddressAutocomplete
              value={formData.secondary_address || ''}
              onChange={(val) => handleChange('secondary_address', val)}
              onAddressSelect={(addr) => {
                handleChange('secondary_address', addr.street || addr.formatted_address);
                if (addr.city) handleChange('secondary_city', addr.city);
                if (addr.state) handleChange('secondary_state', addr.state);
                if (addr.zip) handleChange('secondary_zip', addr.zip);
              }}
              placeholder="Street address (start typing for suggestions)"
            />
          </div>
          <Input value={formData.secondary_city || ''} onChange={(e) => handleChange('secondary_city', e.target.value)} placeholder="City" className="bg-slate-950 border-slate-800 text-white" data-testid="secondary-city-input" />
          <Input value={formData.secondary_state || ''} onChange={(e) => handleChange('secondary_state', e.target.value)} placeholder="State" className="bg-slate-950 border-slate-800 text-white" data-testid="secondary-state-input" />
          <Input value={formData.secondary_zip || ''} onChange={(e) => handleChange('secondary_zip', e.target.value)} placeholder="ZIP" className="bg-slate-950 border-slate-800 text-white" data-testid="secondary-zip-input" />
          <Input value={formData.secondary_contact || ''} onChange={(e) => handleChange('secondary_contact', e.target.value)} placeholder="Contact name" className="bg-slate-950 border-slate-800 text-white" data-testid="secondary-contact-input" />
          <Input value={formData.secondary_phone || ''} onChange={(e) => handleChange('secondary_phone', e.target.value)} placeholder="Contact phone" className="bg-slate-950 border-slate-800 text-white col-span-2" data-testid="secondary-phone-input" />
        </div>
      </div>
    )}

    {/* Additional Client Info */}
    <div className="space-y-2">
      <Label className="text-slate-300">Additional Client Info</Label>
      <Textarea value={formData.additional_client_info || ''} onChange={(e) => handleChange('additional_client_info', e.target.value)} placeholder="Any additional client information..." className="bg-slate-950 border-slate-800 text-white min-h-[60px]" data-testid="additional-client-info-input" />
    </div>

    {/* Forwarder Reference */}
    <div className="space-y-2">
      <Label className="text-slate-300">Forwarder Reference</Label>
      <Input value={formData.forwarder_reference || ''} onChange={(e) => handleChange('forwarder_reference', e.target.value)} placeholder="Forwarder reference number" className="bg-slate-950 border-slate-800 text-white" data-testid="forwarder-reference-input" />
    </div>
  </div>
  );
};

export default {
  SectionHeader,
  AIDocumentUploadSection,
  ContainerInfoSection,
  TerminalChassisSection,
  DatesSection,
  FeesSection,
  OptionsRemarksSection,
  MultipleStopSection,
  AdditionalInfoSection,
  LoadCustomerDeliverySection
};
